/**
 * P0 BULLETPROOF: Connection Status Indicator
 * 
 * Shows users when the server is having issues and automatically
 * processes queued requests when it recovers.
 */
import React, { useState, useEffect } from 'react';
import { Wifi, WifiOff, CloudOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { getOfflineQueueStatus, retryOfflineQueue, resetAllCircuitBreakers } from '../utils/safeFetch';

const ConnectionStatus = () => {
  const [status, setStatus] = useState('connected'); // connected, retrying, offline, recovering
  const [queueLength, setQueueLength] = useState(0);
  const [retryInfo, setRetryInfo] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Listen for retry events
    const handleRetry = (e) => {
      setStatus('retrying');
      setRetryInfo(e.detail);
      setShowBanner(true);
    };

    // Listen for success after retry
    const handleSuccess = (e) => {
      if (e.detail.attempts > 1) {
        setStatus('connected');
        setRetryInfo(null);
        // Hide banner after 3 seconds
        setTimeout(() => setShowBanner(false), 3000);
      }
    };

    // Listen for offline queue updates
    const handleQueueUpdate = (e) => {
      setQueueLength(e.detail.queueLength);
      if (e.detail.queueLength > 0) {
        setStatus('offline');
        setShowBanner(true);
      }
    };

    // Listen for server recovery
    const handleRecovery = (e) => {
      setStatus('recovering');
      setTimeout(() => {
        setStatus('connected');
        setShowBanner(false);
      }, 3000);
    };

    window.addEventListener('apiRetry', handleRetry);
    window.addEventListener('apiSuccess', handleSuccess);
    window.addEventListener('offlineQueueUpdate', handleQueueUpdate);
    window.addEventListener('serverRecovered', handleRecovery);

    // Check initial queue status
    const initialStatus = getOfflineQueueStatus();
    if (initialStatus.length > 0) {
      setQueueLength(initialStatus.length);
      setStatus(initialStatus.serverHealthy ? 'recovering' : 'offline');
      setShowBanner(true);
    }

    return () => {
      window.removeEventListener('apiRetry', handleRetry);
      window.removeEventListener('apiSuccess', handleSuccess);
      window.removeEventListener('offlineQueueUpdate', handleQueueUpdate);
      window.removeEventListener('serverRecovered', handleRecovery);
    };
  }, []);

  const handleRetryNow = () => {
    resetAllCircuitBreakers();
    retryOfflineQueue();
    setStatus('recovering');
  };

  if (!showBanner) return null;

  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 max-w-sm rounded-lg shadow-lg p-4 transition-all duration-300 ${
        status === 'connected' ? 'bg-green-600' :
        status === 'recovering' ? 'bg-blue-600' :
        status === 'retrying' ? 'bg-yellow-600' :
        'bg-red-600'
      } text-white`}
      data-testid="connection-status"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {status === 'connected' && <CheckCircle2 className="w-5 h-5" />}
          {status === 'recovering' && <RefreshCw className="w-5 h-5 animate-spin" />}
          {status === 'retrying' && <Wifi className="w-5 h-5 animate-pulse" />}
          {status === 'offline' && <WifiOff className="w-5 h-5" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">
            {status === 'connected' && 'Connexion rétablie!'}
            {status === 'recovering' && 'Traitement des requêtes en attente...'}
            {status === 'retrying' && `Nouvelle tentative ${retryInfo?.attempt}/${retryInfo?.maxRetries}...`}
            {status === 'offline' && 'Serveur temporairement indisponible'}
          </p>
          
          {queueLength > 0 && status !== 'connected' && (
            <p className="text-xs opacity-90 mt-1">
              {queueLength} requête(s) en attente
            </p>
          )}

          {status === 'offline' && (
            <button
              onClick={handleRetryNow}
              className="mt-2 text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded transition-colors"
            >
              Réessayer maintenant
            </button>
          )}
        </div>

        {/* Close button */}
        {status === 'connected' && (
          <button
            onClick={() => setShowBanner(false)}
            className="flex-shrink-0 opacity-70 hover:opacity-100"
          >
            <span className="sr-only">Fermer</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ConnectionStatus;
